import React, { FC, ChangeEvent, useCallback, useMemo } from 'react';
import { Box, TextField, Button, Stack } from '@mui/material';
import { useRegistrationContext } from '../../contexts/RegistrationContext';
import { useFormValidation } from '../../hooks/useFormValidation';
import { formatRUT } from '../../utils/formatters';

interface PersonalInfoFormProps {
  onNext: () => void;
  onBack: () => void;
}

export const PersonalInfoForm: FC<PersonalInfoFormProps> = ({
  onNext,
  onBack,
}) => {
  const { formData, updateField } = useRegistrationContext();
  const { validateRUT, validatePassword, validatePhone } = useFormValidation();

  // ✅ MANEJADORES OPTIMIZADOS CON USECALLBACK
  const handleRUTChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = event.target;
      const formattedValue = formatRUT(value);
      console.log('formattedValue', formattedValue);
      updateField('RUT', formattedValue);

      // Solo validar si hay contenido
      if (formattedValue.length > 0) {
        const validation = await validateRUT(formattedValue);
        updateField('RUTError', validation.error);
      } else {
        updateField('RUTError', '');
      }
    },
    [updateField, validateRUT]
  );

  const handlePasswordChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const password = event.target.value;
      updateField('password', password);

      // Solo validar si hay confirmación
      if (formData.confirmation) {
        const validation = validatePassword(password, formData.confirmation);
        updateField('passwordError', validation.error);
      } else {
        updateField('passwordError', '');
      }
    },
    [updateField, formData.confirmation, validatePassword]
  );

  const handleConfirmationChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const confirmation = event.target.value;
      updateField('confirmation', confirmation);

      // Solo validar si hay contraseña
      if (formData.password) {
        const validation = validatePassword(formData.password, confirmation);
        updateField('passwordError', validation.error);
      } else {
        updateField('passwordError', '');
      }
    },
    [updateField, formData.password, validatePassword]
  );

  const handlePhoneChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const phone = event.target.value;
      updateField('phone', phone);

      // Solo validar si tiene la longitud completa
      if (phone.length === 11) {
        const validation = await validatePhone(phone);
        updateField('phoneError', validation.error);
      } else {
        updateField('phoneError', '');
      }
    },
    [updateField, validatePhone]
  );

  // ✅ VALIDACIÓN UNIFICADA USANDO EL CONTEXTO
  const isFormValid = useMemo(() => {
    return (
      formData.RUT !== '' &&
      formData.RUTError === '' &&
      formData.name !== '' &&
      formData.lastName !== '' &&
      formData.password !== '' &&
      formData.confirmation === formData.password &&
      formData.passwordError === '' &&
      formData.phone !== '' &&
      formData.phone.length === 11 &&
      formData.phoneError === ''
    );
  }, [formData]);

  return (
    <Box sx={{ paddingX: '5%' }}>
      <Button
        onClick={onBack}
        size="small"
        sx={{ marginY: '2%', fontWeight: 'bold' }}
        variant="text"
      >
        {'← Atras'}
      </Button>

      <TextField
        sx={{ width: '100%', marginY: '2%' }}
        label="RUT"
        variant="outlined"
        value={formData.RUT}
        onChange={handleRUTChange}
        error={formData.RUTError !== ''}
        helperText={formData.RUTError}
        required
      />

      <TextField
        sx={{ width: '100%', marginY: '2%' }}
        label="Teléfono celular"
        variant="outlined"
        value={formData.phone}
        inputProps={{ inputMode: 'numeric' }}
        onChange={handlePhoneChange}
        error={formData.phoneError !== ''}
        helperText={formData.phoneError}
        required
      />

      <TextField
        value={formData.name}
        onChange={e => updateField('name', e.target.value)}
        sx={{ width: '100%', marginY: '2%' }}
        label="Nombre"
        variant="outlined"
        required
      />

      <TextField
        value={formData.lastName}
        onChange={e => updateField('lastName', e.target.value)}
        sx={{ width: '100%', marginY: '2%' }}
        label="Apellido"
        variant="outlined"
        required
      />

      <TextField
        value={formData.password}
        onChange={handlePasswordChange}
        sx={{ width: '100%', marginY: '2%' }}
        label="Contraseña"
        type="password"
        required
      />

      <TextField
        value={formData.confirmation}
        onChange={handleConfirmationChange}
        sx={{ width: '100%', marginY: '2%' }}
        label="Confirma tu contraseña"
        type="password"
        error={formData.passwordError !== ''}
        helperText={formData.passwordError}
        required
      />

      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={12}
      >
        <Button
          disabled={!isFormValid}
          onClick={onNext}
          size="large"
          sx={{ marginY: '2%', fontWeight: 'bold', borderRadius: '50px' }}
          variant="contained"
        >
          {'Siguiente →'}
        </Button>
      </Stack>
    </Box>
  );
};
